/**
 * Referral / invite service — single entry point for all referral flows.
 *
 * - Stores a pending referral code in localStorage when a visitor lands on
 *   /invite/:code (so it survives across signup → email confirmation → login).
 * - Claims the code once a Supabase session exists (via SECURITY DEFINER RPC).
 * - Reads the current user's invite stats and sends user-to-user invites
 *   through the `invite-send` edge function.
 */
import { supabase } from '@/integrations/supabase/client';

const STORAGE_KEY = 'scuvio.pendingReferralCode';
const BUDDY_TOKEN_KEY = 'scuvio.pendingBuddyInviteToken';

export const REFERRAL_CODE_REGEX = /^[A-Z0-9]{8,12}$/i;
/** Buddy invite tokens are 48-char hex strings generated server-side. */
export const BUDDY_INVITE_TOKEN_REGEX = /^[a-f0-9]{32,64}$/i;

export function isBuddyInviteToken(value: string | undefined | null): boolean {
  return !!value && BUDDY_INVITE_TOKEN_REGEX.test(value.trim());
}

export function isReferralCode(value: string | undefined | null): boolean {
  return !!value && REFERRAL_CODE_REGEX.test(value.trim());
}

export function setPendingReferralCode(code: string | null | undefined): void {
  if (!code) return;
  const clean = code.trim().toUpperCase();
  if (!REFERRAL_CODE_REGEX.test(clean)) return;
  try { localStorage.setItem(STORAGE_KEY, clean); } catch { /* ignore */ }
}

export function getPendingReferralCode(): string | null {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}

export function clearPendingReferralCode(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

/* ── Pending Buddy Invite Token (survives signup → email confirm → login) ── */

export function setPendingBuddyToken(token: string | null | undefined): void {
  if (!token) return;
  const clean = token.trim().toLowerCase();
  if (!BUDDY_INVITE_TOKEN_REGEX.test(clean)) return;
  try { localStorage.setItem(BUDDY_TOKEN_KEY, clean); } catch { /* ignore */ }
}

export function getPendingBuddyToken(): string | null {
  try { return localStorage.getItem(BUDDY_TOKEN_KEY); } catch { return null; }
}

export function clearPendingBuddyToken(): void {
  try { localStorage.removeItem(BUDDY_TOKEN_KEY); } catch { /* ignore */ }
}

/**
 * Try to redeem a pending buddy invite token for the currently signed-in user.
 * Server-side ON CONFLICT keeps it idempotent and also creates the referral
 * link (inviter = invite.created_by) if not already set.
 */
export async function claimPendingBuddyInvite(): Promise<{ ok: boolean; error?: string } | null> {
  const token = getPendingBuddyToken();
  if (!token) return null;
  const { data, error } = await supabase.rpc('redeem_buddy_invite', { _invite_token: token });
  if (error) return { ok: false, error: error.message };
  const r = (data ?? {}) as { ok?: boolean; error?: string };
  // Conclusive outcomes — clear the token so we don't keep retrying forever.
  const conclusive = ['invalid_invite', 'self_invite', 'expired', 'max_uses_reached',
    'blocked', 'already_connected', 'already_pending'];
  if (r.ok || (r.error && conclusive.includes(r.error))) {
    clearPendingBuddyToken();
  }
  return { ok: !!r.ok, error: r.error };
}

export interface ClaimResult {
  ok: boolean;
  alreadyClaimed?: boolean;
  inviterId?: string;
  error?: string;
}

/**
 * Try to claim a pending referral code for the currently signed-in user.
 * Safe to call multiple times — server-side is idempotent.
 */
export async function claimPendingReferral(): Promise<ClaimResult | null> {
  const code = getPendingReferralCode();
  if (!code) return null;
  const { data, error } = await supabase.rpc('claim_referral', { _code: code });
  if (error) return { ok: false, error: error.message };
  const result = (data ?? {}) as { ok?: boolean; already_claimed?: boolean; error?: string; inviter_id?: string };
  // Clear on any conclusive outcome (success or definitive failure like invalid_code/self_referral)
  if (result.ok || ['invalid_code', 'self_referral', 'unauthenticated'].includes(result.error ?? '')) {
    clearPendingReferralCode();
  }
  return {
    ok: !!result.ok,
    alreadyClaimed: !!result.already_claimed,
    inviterId: result.inviter_id,
    error: result.error,
  };
}

export interface InviteStats {
  referralCode: string;
  counts: {
    registered: number;
    verified: number;
    active: number;
    total: number;
    /** Eindeutige Empfänger, an die per E-Mail eingeladen wurde. */
    invited: number;
    /** Eingeladene Empfänger, die noch keinen Account haben. */
    pending: number;
  };
}

export async function getMyInviteStats(): Promise<InviteStats | null> {
  const { data, error } = await supabase.rpc('get_my_invite_stats');
  if (error) return null;
  const r = (data ?? {}) as { ok?: boolean; referral_code?: string; counts?: InviteStats['counts'] };
  if (!r.ok || !r.referral_code) return null;
  return {
    referralCode: r.referral_code,
    counts: {
      registered: r.counts?.registered ?? 0,
      verified: r.counts?.verified ?? 0,
      active: r.counts?.active ?? 0,
      total: r.counts?.total ?? 0,
      invited: r.counts?.invited ?? 0,
      pending: r.counts?.pending ?? 0,
    },
  };
}

export interface SendInvitePayload {
  recipient_email: string;
  recipient_name?: string;
  message?: string;
}

export async function sendInviteEmail(payload: SendInvitePayload): Promise<{ ok: boolean; error?: string; inviteUrl?: string }> {
  const { data, error } = await supabase.functions.invoke('invite-send', { body: payload });
  if (error) return { ok: false, error: error.message };
  const r = (data ?? {}) as { ok?: boolean; invite_url?: string; error?: string };
  return { ok: !!r.ok, inviteUrl: r.invite_url, error: r.error };
}

export function buildInviteUrl(code: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://scuv.io';
  return `${origin}/invite/${code.toUpperCase()}`;
}
